import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface DeliveryProtectedRouteProps {
  children: React.ReactNode;
  // When true, the route requires the MANAGER role, not just portal access. The
  // existing pages (dashboard, leads, map, detail) expose the whole book, so they
  // are manager-only; a driver is denied. Defaults to true so a new delivery route
  // is manager-gated unless deliberately opened to drivers (the future route view).
  requireManager?: boolean;
}

/**
 * Guard for the Delivery Portal. Modeled on ProtectedRoute (the admin guard),
 * NOT on the Mill/Store guards — this checks a real permission, not just
 * authentication. The Delivery Portal holds customer home addresses, away-dates,
 * gate codes and dog warnings, so it must be permission-gated.
 */
const DeliveryProtectedRoute: React.FC<DeliveryProtectedRouteProps> = ({ children, requireManager = true }) => {
  const { isAuthenticated, user, loading, canAccessDeliveryPortal, canManageDelivery } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Must be authenticated first.
  if (!isAuthenticated || !user) {
    localStorage.removeItem('rbf_user_data');
    localStorage.removeItem('user');
    console.log('[DeliveryProtectedRoute] Not authenticated - redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  // Then must hold the required delivery role. Portal access = any role; manager
  // pages need the manager role. A driver hitting a manager page is denied.
  const permitted = requireManager ? canManageDelivery : canAccessDeliveryPortal;
  if (!permitted) {
    console.log('[DeliveryProtectedRoute] User lacks required delivery access - access denied');
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        textAlign: 'center',
        padding: '2rem'
      }}>
        <h1 style={{ color: '#dc2626', marginBottom: '1rem' }}>Access Denied</h1>
        <p style={{ marginBottom: '1rem' }}>You do not have permission to access the Delivery Portal.</p>
        <p style={{ color: '#666' }}>
          This access is granted per user. Ask an administrator if you need it.
        </p>
      </div>
    );
  }

  return <>{children}</>;
};

export default DeliveryProtectedRoute;
